import { getInvoices } from './generated/invoices/invoices';
import { getOffers } from './generated/offers/offers';
import { getTextSnippets as getTextSnippetsClient } from './generated/text-snippets/text-snippets';
import type {
  FacilityFlowCoreModulesTextSnippetsDtosTextSnippetDto as WireTextSnippetDto,
  FacilityFlowCoreModulesTextSnippetsEntitiesTextSnippetCategory as WireTextSnippetCategory,
} from './generated/models';
import type {
  CreateTextSnippetRequest,
  GetTextSnippetsParams,
  RenderInvoiceSnippetRequest,
  RenderInvoiceSnippetResponse,
  RenderOfferSnippetRequest,
  RenderOfferSnippetResponse,
  TextSnippetCategory,
  TextSnippetDto,
  UpdateTextSnippetRequest,
} from '../types/textSnippets';
import { TEXT_SNIPPET_CATEGORY_CODES, isTextSnippetCategory } from '../types/textSnippets';

// why: the generated client types `category` as the int enum literal union (1–8), while the
// app works with the string categories — this wrapper only translates between the two
// (codes live in the type module, see TEXT_SNIPPET_CATEGORY_CODES) and delegates every
// call to the generated Orval client.
const INT_TO_CATEGORY: Record<number, TextSnippetCategory> = Object.fromEntries(
  (Object.entries(TEXT_SNIPPET_CATEGORY_CODES) as [TextSnippetCategory, number][]).map(
    ([category, code]) => [code, category],
  ),
);

function toWireCategory(category: TextSnippetCategory): WireTextSnippetCategory {
  return TEXT_SNIPPET_CATEGORY_CODES[category] as WireTextSnippetCategory;
}

function normalizeCategory(value: unknown): TextSnippetCategory {
  if (typeof value === 'number' && value in INT_TO_CATEGORY) {
    return INT_TO_CATEGORY[value];
  }
  if (isTextSnippetCategory(value)) {
    return value;
  }
  return 'OfferIntroduction';
}

function fromWire(dto: WireTextSnippetDto): TextSnippetDto {
  return {
    id: dto.id ?? '',
    category: normalizeCategory(dto.category),
    name: dto.name ?? '',
    body: dto.body ?? '',
    isDefault: dto.isDefault ?? false,
    isActive: dto.isActive ?? true,
    sortOrder: dto.sortOrder ?? 0,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? null,
  };
}

export const getTextSnippets = async (params?: GetTextSnippetsParams) => {
  const wireParams = params
    ? {
        ...(params.category !== undefined ? { category: toWireCategory(params.category) } : {}),
        ...(params.isActive !== undefined ? { isActive: params.isActive } : {}),
      }
    : undefined;
  const result = await getTextSnippetsClient().getApiV1TextSnippets(wireParams);
  return (result || []).map(fromWire);
};

export const getTextSnippetById = async (id: string) => {
  const result = await getTextSnippetsClient().getApiV1TextSnippetsId(id);
  return fromWire(result);
};

export const createTextSnippet = async (data: CreateTextSnippetRequest) => {
  const result = await getTextSnippetsClient().postApiV1TextSnippets({
    ...data,
    category: toWireCategory(data.category),
  });
  return fromWire(result);
};

export const updateTextSnippet = async (id: string, data: UpdateTextSnippetRequest) => {
  const result = await getTextSnippetsClient().putApiV1TextSnippetsId(id, data);
  return fromWire(result);
};

export const deleteTextSnippet = (id: string) =>
  getTextSnippetsClient().deleteApiV1TextSnippetsId(id);

export const setTextSnippetDefault = async (id: string) => {
  const result = await getTextSnippetsClient().postApiV1TextSnippetsIdSetDefault(id);
  return fromWire(result);
};

export const renderOfferSnippet = async (
  data: RenderOfferSnippetRequest,
): Promise<RenderOfferSnippetResponse> => {
  const response = await getOffers().postApiV1OffersRenderSnippet(data);
  return { renderedBody: response.renderedBody ?? '' };
};

export const renderInvoiceSnippet = async (
  data: RenderInvoiceSnippetRequest,
): Promise<RenderInvoiceSnippetResponse> => {
  const response = await getInvoices().postApiV1InvoicesRenderSnippet(data);
  return { renderedBody: response.renderedBody ?? '' };
};
